class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach(animation => animation.play());
    } else {
      this.animations.forEach(animation => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

// TODO: Clean up / remove
class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '')
      return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

class DetailsDisclosureBase extends HTMLElement {
  constructor(focusOut = true) {
    super();
    this.name = this.dataset.name ?? 'disclosure';
    this.containerClasses = [`has-open-${this.name}`];
    this.focusOut = focusOut;
    this.onSummaryClick = this.handleSummaryClick.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onKeyUp = this.handleKeyup.bind(this);

    this.disclosureContainer = this.closest('[data-disclosure-container]');
    this.detailsElement = this.querySelector('details');
    this.summaryElement = this.querySelector('summary');
  }

  connectedCallback() {
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  bindEvents() {
    this.summaryElement.addEventListener('click', this.onSummaryClick);

    if (this.focusOut) {
      this.detailsElement.addEventListener('focusout', this.onFocusOut);
    }

    this.detailsElement.addEventListener('keyup', this.onKeyUp);
  }

  unbindEvents() {
    this.summaryElement.removeEventListener('click', this.onSummaryClick);

    if (this.focusOut) {
      this.detailsElement.removeEventListener('focusout', this.onFocusOut);
    }

    this.detailsElement.removeEventListener('keyup', this.onKeyUp);
  }

  isOpen() {
    return this.detailsElement.hasAttribute('open');
  }

  handleSummaryClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open();
  }

  handleFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  handleKeyup(event) {
    event.code.toUpperCase() === 'ESCAPE' && this.close();
  }

  open() {
    this.detailsElement.setAttribute('open', true);
    this.disclosureContainer.classList.add(...this.containerClasses);
  }

  close() {
    this.detailsElement.removeAttribute('open');
    this.summaryElement.setAttribute('aria-expanded', false);
    this.disclosureContainer.classList.remove(...this.containerClasses);
  }
}

class MenuDisclosure extends DetailsDisclosureBase {
  constructor() {
    super();

    this.item = this.closest('li');
    this.child = this.querySelector('summary + div');
    this.siblings = Array.from(this.disclosureContainer.querySelectorAll('menu-disclosure')).filter(
      sibling => sibling !== this
    );
    this.allChildren = Array.from(
      this.disclosureContainer.querySelectorAll('menu-disclosure summary + div')
    );
    this.background = this.disclosureContainer.querySelector(
      '[data-menu-background]'
    );
    this.sectionId = this.closest('.shopify-section').id.replace('shopify-section-', '');

    this.header = document.querySelector('.section--header');
    this.headerWrapper = document.querySelector('.header-wrapper');
    this.rootLinks = this.disclosureContainer.querySelectorAll(
      '.header__menu-item--link, .header__icon'
    );

    this.timeline = gsap.timeline({ paused: true });
    this.openTimeline = gsap.timeline({ paused: true });
    this.switchTimeline = gsap.timeline({
      paused: true,
      onComplete: () => this.disclosureContainer.classList.remove('is-switching'),
    });
    this.onMouseOver = this.handleMouseOver.bind(this);
    this.onClose = Shopify.designMode ? this.themeEditorHandleClose.bind(this) : this.handleClose.bind(this);
    this.onRootLinkHover = this.handleRootLinkHover.bind(this);
    this.hoverIntentListeners = null;

    this.getHeaderHeight();
    this.buildTimelines();
  }

  bindEvents() {
    super.bindEvents();

    if (typeof window.hoverintent !== 'undefined') {
      const main = hoverintent(this, this.onMouseOver, () => {});
      main.options({ interval: 50 });

      const rootLinks = [];
      this.rootLinks.forEach(link => {
        const rootLinkListener = hoverintent(link, this.onRootLinkHover, () => {});
        rootLinkListener.options({ interval: 50 });
        rootLinks.push(rootLinkListener);
      });

      this.hoverIntentListeners = { main, rootLinks };
    } else {
      this.addEventListener('mouseenter', this.onMouseOver);
      this.rootLinks.forEach(link => link.addEventListener('mouseenter', this.onRootLinkHover));
    }

    if (Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.onBlockSelect.bind(this));
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect.bind(this));
      document.addEventListener('shopify:section:load', this.onSectionLoad.bind(this));
    }

    this.header.addEventListener('mouseleave', this.onClose);
    this.isSwitching = false;
  }

  get editorBlocks() {
    this._editorBlocks ??= [...this.querySelectorAll('[data-shopify-editor-block]')];
    return this._editorBlocks;
  }

  get editorBlockIds() {
    this._editorBlocksData ??= this.editorBlocks.map(block => JSON.parse(block.dataset.shopifyEditorBlock));
    return this._editorBlocksData.map(block => block.domId);
  }

  getBlockById(id) {
    const index = this.editorBlockIds.indexOf(id);
    return this.editorBlocks[index];
  }

  lockBody() {
    document.body.style.overflow = 'clip';
    const { scrollX, scrollY } = window;
    console.log('LOCKING BODY', scrollX, scrollY)
    this.revertScrollToSavedState = (e) => {
      e.preventDefault();
      window.scrollTo(scrollX, scrollY)
    };

    window.addEventListener('scroll', this.revertScrollToSavedState);
  }

  unlockBody() {
    document.body.style.overflow = '';
    window.removeEventListener('scroll', this.revertScrollToSavedState);
  }

  triggerBodyLock() {
    this.clearBodyLockTimeout();

    this.lockBody();
    this.bodyLockTimeout = setTimeout(this.triggerBodyUnlock.bind(this), 1000)
  }

  triggerBodyUnlock() {
    this.unlockBody();
    this.clearBodyLockTimeout();
  }

  clearBodyLockTimeout() {
    clearTimeout(this.bodyLockTimeout);
    this.bodyLockTimeout = undefined;
  }

  onSectionLoad(event) {
    if (event.detail.sectionId !== this.sectionId) return;
    console.log('SECTION LOADED', event, event.detail)
    console.log('SECTION LOADED BLOCK SELECTION', window.EditorState.block)

    this.lockBody();

    const block = this.getBlockById(window.EditorState.blockId);
    if (block) {
      console.log('BLOCK', block)
      this.open(false);
    }
  }

  onBlockSelect(event) {
    const block = this.getBlockById(event.detail.blockId);
    if (!block) return;
    console.log('SELECTING', block)
    // Open menu item on block selection
    this.open();

    // Prevent scroll jump on block updates
    // if (this.detailsElement.classList.contains('mega-menu')) {
      this.lockBody();
    // }

    // Hack to prevent block outline from collapsing to <1px height
    this.blockOutlineStyle = document.createElement('style');
    this.blockOutlineStyle.innerHTML = `
      shopify-editor-preview-block shopify-editor-preview-inspector-outline {
        max-height: none !important;
      }
    `;
    this.editorInterface ??= document.querySelector('shopify-editor-interface');
    this.editorInterface.shadowRoot.prepend(this.blockOutlineStyle);
    this.header.style.position = 'static';
  }

  onBlockDeselect(event) {
    const block = this.getBlockById(event.detail.blockId);
    if (!block) return;

    // Remove block outline style hack
    this.blockOutlineStyle?.remove();
    setTimeout(() => {
      console.log('BLOCK DESelected', window.EditorState.block)
      // Close menu item on block deselection
      if (!window.EditorState.block?.element.closest('menu-disclosure')) {
        this.close();
        this.header.style.position = '';
      }
    })
  }

  unbindEvents() {
    super.unbindEvents();

    if (this.hoverIntentListeners) {
      this.hoverIntentListeners.main.remove();
      this.hoverIntentListeners.rootLinks.forEach(listener => listener.remove());
      this.hoverIntentListeners = null;
    } else {
      this.removeEventListener('mouseenter', this.onMouseOver);
      this.rootLinks.forEach(link => link.removeEventListener('mouseenter', this.onRootLinkHover));
    }

    if (Shopify.designMode) {
      document.removeEventListener('shopify:block:select', this.onBlockSelect.bind(this));
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect.bind(this));
      document.removeEventListener('shopify:section:load', this.onSectionLoad.bind(this));
    }

    this.header.removeEventListener('mouseleave', this.onClose);
  }

  handleSummaryClick(event) {
    const innerLink = event.currentTarget.querySelector('a');
    if (innerLink) return;

    event.preventDefault();
    this.isOpen() ? this.close() : this.open();
  }

  getSubnavHeight() {
    return this.child.offsetHeight;
  }

  buildTimeline(timeline, switching) {
    // TODO: Consider auto-updating subnav height on window resize
    timeline
      .to(this.background, {
        y: this.getSubnavHeight.bind(this),
        duration: switching ? 0.35 : 0.5,
        ease: 'power2.inOut',
      })
      .to(
        this.allChildren,
        {
          opacity: 0,
          duration: 0.35,
          ease: 'power2.out',
        },
        0
      )
      .fromTo(
        this.child,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.35,
          ease: switching ? 'power2.in' : 'power2.inOut',
        },
        switching ? 0 : 0.2
      );

      // TODO: Maybe check selected block?
      // if (Shopify.designMode) {
      //   timeline.to(this.header, {
      //     marginBottom: this.getSubnavHeight.bind(this),
      //     duration: switching ? 0.35 : 0.5,
      //     ease: 'power2.inOut',
      //   }, 0)
      // }

      if (!switching) {
        timeline.fromTo(
          this.child,
          {
            y: -50,
          },
          {
            y: 0,
            duration: 0.5,
            ease: 'power2.inOut',
            onComplete: () => gsap.set(this.child, { clearProps: 'y' }),
          },
          0.05
        );
      } else {
        gsap.set(this.child, { clearProps: 'y' });
      }
  }

  hideSiblings() {
    this.allChildren
      .filter(child => child !== this.child)
      .forEach(child => gsap.set(child, { opacity: 0 }));
  }

  buildTimelines() {
    this.buildTimeline(this.openTimeline, false);
    this.buildTimeline(this.switchTimeline, true);
  }

  isOpen() {
    return this.item.classList.contains('is-open');
  }

  handleMouseOver() {
    this.open();
  }

  handleClose() {
    this.cancelClose();

    this.closingTime = setTimeout(() => {
      this.close();
    }, 150);
  }

  themeEditorHandleClose() {
    this.cancelClose();

    this.closingTime = setTimeout(() => {
      // console.log('CLOSING? Current block', window.EditorState.block)
      const selectedMenuDisclosure = window.EditorState.block?.element.closest('menu-disclosure');
      if (selectedMenuDisclosure) {
        selectedMenuDisclosure.open();
        return;
      }

      this.close();
    }, 150);
  }

  handleRootLinkHover() {
    if (this.isOpen()) {
      if (Shopify.designMode) {
        this.themeEditorHandleClose();
      } else {
        this.handleClose();
      }
    }
  }

  getHeaderHeight() {
    if (
      document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') === ''
    ) {
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(this.headerWrapper.getBoundingClientRect().bottom)}px`
      );
      document.documentElement.style.setProperty(
        '--header-height',
        `${Math.floor(this.header.offsetHeight)}px`
      );
    }
  }

  open(animate = true) {
    this.getHeaderHeight();

    this.isSwitching = this.hasOpenSibling();
    this.querySelector('summary').setAttribute('aria-expanded', true);

    this.cancelClose();
    this.closeSiblings();

    if (this.isOpen()) return;

    super.open();

    if (this.isSwitching) {
      this.disclosureContainer.classList.add('is-switching');
    }

    this.item.classList.add('is-open');
    this.headerWrapper.preventHide = true;

    setTimeout(() => {
      this.disclosureContainer.classList.add('has-menu-buffer');

      const timeline = this.isSwitching ? this.switchTimeline : this.openTimeline;
      if (animate) {
        timeline.invalidate().play(0);
      } else {
        timeline.invalidate().progress(1, false).pause();
      }
    });
  }

  hasOpenSibling() {
    return this.siblings.some(sibling => sibling.isOpen());
  }

  cancelClose() {
    clearTimeout(this.closingTime);
    this.closingTime = undefined;
  }

  closeSiblings() {
    this.siblings.forEach(sibling => sibling.close());
  }

  close() {
    this.cancelClose();

    if (!this.isOpen()) return;

    this.headerWrapper.preventHide = false;
    this.item.classList.remove('is-open');
    this.summaryElement.setAttribute('aria-expanded', false);
    this.querySelector('summary').setAttribute('aria-expanded', false);

    setTimeout(() => {
      if (!this.hasOpenSibling()) {
        this.disclosureContainer.classList.remove(...this.containerClasses);
        this.timeline.pause();
        gsap.to(this.child, {
          duration: 0.5,
          y: -60,
          ease: 'power2.inOut',
          onComplete: () => gsap.set(this.child, { clearProps: 'y' }),
        });
        gsap.to(this.child, {
          opacity: 0,
          duration: 0.4,
          ease: 'power2.inOut',
        });

        gsap.to(this.background, {
          y: 0,
          duration: 0.5,
          ease: 'power2.inOut',
          delay: 0.05,
        });
      }
    });

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = time => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else if (!this.isOpen()) {
        this.detailsElement.removeAttribute('open');

        if (!this.hasOpenSibling()) {
          this.disclosureContainer.classList.remove('has-menu-buffer');
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-disclosure', MenuDisclosure);
